import { useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import { Image } from "react-bootstrap";
import _ from "lodash";
import { useDispatch } from "react-redux";
import cx from "classnames";
import { Link } from "react-router-dom";

import InfiniteScroll from "src/components/infiniteScroll";
import Thumbnail from "src/components/thumbnail";
import { Asset } from "src/types/assets";
import { FileService } from "src/utils/file.service";
import { fetchDealRoomSharedAssets, fetchDealRoomSharedCollections } from "src/redux/actions/dealRoom";
import { DealRoomContext } from "../../context";

import styles from "./styles.module.scss";
import EmptyMessage from "src/components/emptyMessage";

const AssetDefaultIcon = '/images/defaultIcons/default_asset.jpg';
const EmptyResult = '/images/assets/assetsEmptyMessage.svg';

const AssetPlaceholder = () => {
  return (
    <div className={cx(styles.assetPlaceholder)}>
      <div className={cx(styles.assetPlaceholderCard, styles.block)}></div>
      <div className={cx(styles.assetPlaceholderText, styles.block)}></div>
    </div>
  )
}

const CollectionPlaceholder = () => {
  return (
    <div className={cx(styles.assetPlaceholder)}>
      <div className={cx(styles.collectionPlaceholderCard, styles.block)}></div>
      <div className={cx(styles.assetPlaceholderText, styles.block)}></div>
    </div>
  )
};

const ContentShared = () => {
  const dispatch = useDispatch();
  const { id: dealRoomId, dealId } = useContext(DealRoomContext);

  const sharedAssetsData = useSelector((state: any) => {
    return _.get(state, `dealRooms.dealRoom['${dealRoomId}'].dealInsights.sharedAssets`, null);
  });
  const sharedCollectionsData = useSelector((state: any) => {
    return _.get(state, `dealRooms.dealRoom['${dealRoomId}'].dealInsights.sharedCollections`, null);
  });

  const sharedAssets = _.get(sharedAssetsData, 'items', []);
  const sharedAssetsLoading = _.get(sharedAssetsData, 'loading', false);
  const sharedAssetsLoaded = _.get(sharedAssetsData, 'loaded', false);
  const sharedAssetsCurrentPage = _.get(sharedAssetsData, 'pagination.currentPage', -1);
  const sharedAssetsTotalPages = _.get(sharedAssetsData, 'pagination.totalPages', 0);
  const sharedAssetsTotalRecordsCount = _.get(sharedAssetsData, 'pagination.totalRecordsCount', 0);
  const sharedAssetsHasMoreItems = _.get(sharedAssetsData, 'pagination.hasMoreItems', false);

  const sharedCollections = _.get(sharedCollectionsData, 'items', []);
  const sharedCollectionsLoading = _.get(sharedCollectionsData, 'loading', false);
  const sharedCollectionsLoaded = _.get(sharedCollectionsData, 'loaded', false);

  useEffect(() => {
    if (!sharedAssetsLoading && !sharedAssetsLoaded) {
      dispatch(fetchDealRoomSharedAssets({
        dealRoomId: dealRoomId ?? '',
        dealId: dealId ?? '',
        page: 0
      }));
    }
  }, [sharedAssetsLoading, sharedAssetsLoaded]);

  useEffect(() => {
    if (!sharedCollectionsLoading && !sharedCollectionsLoaded) {
      dispatch(fetchDealRoomSharedCollections({
        dealRoomId: dealRoomId ?? '',
        dealId: dealId ?? '',
      }));
    }
  }, [sharedCollectionsLoading]);

  const loadMoreAssets = () => {
    const nextPage = sharedAssetsCurrentPage + 1;
    if (sharedAssetsLoading || nextPage >= sharedAssetsTotalPages) {
      return;
    }

    dispatch(fetchDealRoomSharedAssets({
      dealRoomId: dealRoomId ?? '',
      dealId: dealId ?? '',
      page: nextPage
    }));
  }
  
  return (
    <div className={styles.container}>
      {
        !sharedAssetsLoading && sharedAssetsLoaded && _.size(sharedAssets) === 0 && _.size(sharedCollections) === 0 && (
          <EmptyMessage
            className={styles.emptyMessageContainer}
            heading="No Content Shared"
            image={EmptyResult}
          />
        )
      }

      <div className={styles.section}>
        {_.size(sharedAssets) > 0 && <h6 className={styles.heading}>{`Asset${sharedAssetsTotalRecordsCount > 1 ? 's' : ''} (${sharedAssetsTotalRecordsCount})`}</h6>}
        <div className={styles.sharedContainer}>
          <InfiniteScroll
            className={cx(styles.gridContainer, styles.assetGridContainer)}
            loading={sharedAssetsLoading}
            hasMore={sharedAssetsHasMoreItems}
            loadMore={loadMoreAssets}
            loader={() => {
              if (_.size(sharedAssets) > 0) {
                return <AssetPlaceholder />
              }

              return (
                <>
                  <AssetPlaceholder />
                  <AssetPlaceholder />
                  <AssetPlaceholder />
                  <AssetPlaceholder />
                </>
              );
            }}
          >
            {
              _.size(sharedAssets) > 0 && (_.map(sharedAssets, (asset: any) => {
                return (
                  <Link
                    to={`/assets/${asset.id}`}
                    target="_blank"
                    key={asset?.id}
                    className={styles.link}
                  >
                    <div className={styles.assetCard}>
                      <AssetIcon key={asset.id} asset={asset} />
                      <div style={{ maxWidth: '100px' }} className={cx(styles.assetName, styles.truncatedText)}>
                        {asset.name}
                      </div>
                    </div>
                  </Link>
                )
              }))
            }
          </InfiniteScroll>
        </div>
      </div>

      <div className={styles.section}>
        {_.size(sharedCollections) > 0 && <h6 className={styles.heading}>{`Collection${_.size(sharedCollections) > 1 ? 's' : ''} (${_.size(sharedCollections)})`}</h6>}
        <div className={styles.sharedContainer}>
          <div className={cx(styles.gridContainer, styles.collectionGridContainer)}>
            {
              sharedCollectionsLoading && (
                <>
                  <CollectionPlaceholder />
                  <CollectionPlaceholder />
                </>
              )
            }
            {
              !sharedCollectionsLoading && _.size(sharedCollections) > 0 && (_.map(sharedCollections, (collection: any) => {
                return (
                  <Link
                    to={`/collections/${collection.id}`}
                    className={cx(styles.collectionContainer, styles.link)}
                    key={collection.id}
                    target="_blank"
                  >
                    <div className={cx(styles.assetCard, styles.collectionCard)}>
                      <Image className={styles.collectionIcon} src={_.get(collection, "icon.thumbnail", AssetDefaultIcon)} />
                      <div className={cx(styles.assetName, styles.truncatedText)}>{collection.name}</div>
                    </div>
                  </Link>
                )
              }))
            }
          </div>
        </div>
      </div>
    </div>
  )
}

interface AssetIconProps {
  asset: Asset
}

const AssetIcon: React.FC<AssetIconProps> = ({ asset }) => {
  return (
    <div className={cx(styles.assetContainer)}>
      <div className={cx(styles.iconWrapper, styles.card)}>
        <Thumbnail
          media={asset.icon}
          width={600}
          className={cx(styles.thumbnail)}
          alt=""
          defaultImage="/images/assets/default_asset.jpg"
        />
        <span className={styles.assetTypeWrapper}>
          <span className={styles.assetType}>{_.get(asset, "type") || FileService.getFileType(_.get(asset, "metadata", null))}</span>
        </span>
      </div>
    </div>
  )
}

export default ContentShared;